import { regexes } from './regexes.const';
import { dateToInsurenetDateString } from '../mappers/date';
import { InsuredObject } from '@models/insured-object';
import { addYears, isBefore, parse } from 'date-fns';
import { Product } from '@models/product';
import { Insurant } from '@models/insurant';
import { OrderState } from 'app/order/models';

const today = new Date();

export function findViableContractStartDate(mode: 'month' | 'day'): string {
    if (mode === 'month') {
        if (today.getDate() < 15) {
            return dateToInsurenetDateString(new Date(today.getFullYear(), today.getMonth(), 1));
        } else {
            return dateToInsurenetDateString(new Date(today.getFullYear(), today.getMonth() + 1, 1));
        }
    } else {
        return dateToInsurenetDateString(new Date());
    }
}

export function compareProducts(a: Product, b: Product): boolean {
    return a && b && a.insuranceTypeId === b.insuranceTypeId && a.slug === b.slug;
}

export function compareInsuredObjects(a: InsuredObject[], b: InsuredObject[]): boolean {
    if (a.length !== b.length) {
        return false;
    }
    return !a.find(
        (item, index) =>
            item.date !== b[index].date ||
            item.description !== b[index].description ||
            item.detail !== b[index].detail ||
            item.person !== b[index].person ||
            item.value !== b[index].value ||
            item.model !== b[index].model ||
            item.type !== b[index].type,
    );
}

export function compareStates(a: OrderState, b: OrderState, options = { checkOnlyBetweenSteps: false }): boolean {
    if (!a || !b) {
        return false;
    }
    if (options.checkOnlyBetweenSteps && a.currentStep === b.currentStep) {
        return true;
    }
    // compare slugs:
    if (a.productSlug !== b.productSlug) {
        return false;
    }
    // compare insurants:
    if (a.insurants && b.insurants) {
        if (a.insurants.length !== b.insurants.length) {
            return false;
        } else if (!compareInsurants(a.insurants, b.insurants)) {
            return false;
        }
    } else if ((!a.insurants && b.insurants) || (a.insurants && !b.insurants)) {
        return false;
    }
    // compare insured objects:
    if (a.insuredObjects && b.insuredObjects) {
        if (a.insuredObjects.length !== b.insuredObjects.length) {
            return false;
        } else if (compareInsuredObjects(a.insuredObjects, b.insuredObjects)) {
            return false;
        }
    } else if ((!a.insuredObjects && b.insuredObjects) || (a.insuredObjects && !b.insuredObjects)) {
        return false;
    }
    return true;
}

export function compareInsurants(a: Insurant[], b: Insurant[]): boolean {
    for (let i = 0; i < a.length; i++) {
        const insurant = b.find(
            (item) =>
                item.dateOfBirth === a[i].dateOfBirth &&
                item.insuranceRankId === a[i].insuranceRankId &&
                item.firstName === a[i].firstName &&
                item.lastName === a[i].lastName,
        );
        if (!insurant) {
            return false;
        }
    }
    return true;
}

/**
 * @description takes a birthday in insurenet date format and
 *  returns wether or not the person is still below 18 years
 *  today.
 * @param {string} birthday day of the persons birth as string in format dd.mm.yyyy
 * @returns {boolean} true if the person is under 18 years old, false if older
 */
export function isChild(birthday: string): boolean {
    if (!regexes.insureNetDateFormat.test(birthday)) {
        throw new Error('Invalid date format. Must be dd.mm.yyyy');
    }
    const bday = parse(birthday, 'dd.MM.yyyy', today);
    if (today.getFullYear() - bday.getFullYear() === 18) {
        return isBefore(today, addYears(bday, 18));
    }
    return today.getFullYear() - bday.getFullYear() < 18;
}

export const toFixedFloat: (value: string | number, decimals?: number, separator?: string) => number = (
    value: string | number,
    decimals: number = 2,
    separator: string = ',',
) => {
    const decimalsRegex = new RegExp(`(${separator}[0-9]{${decimals}})[0-9]*$`);
    return parseFloat((value + '').replace(decimalsRegex, '$1').replace(',', '.'));
};

export const addDecimals: (value: string, decimals?: number, separator?: string) => string = (
    value: string,
    decimals: number = 2,
    separator: string = ',',
) => {
    if (!value.includes(separator)) {
        value += separator;
    }
    const i = value.indexOf(separator);
    while (value.length < i + decimals + 1) {
        value += '0';
    }
    return value;
};

export const toPriceString: (value: number | string | null, decimals?: number, separator?: string) => string = (
    value: number | string | null,
    decimals: number = 2,
    separator: string = ',',
) => {
    if (value === null || value === '') {
        return '';
    }
    value = parseFloat((value + '').replace(separator, '.'));
    value = (value + '').replace('.', separator);
    return addDecimals(value, decimals, separator);
};

export function splitFileName(fileName: string): RegExpMatchArray {
    // Matches filenames with or without extension
    // First capture group contains the filename (everything before the last dot, if there is one)
    // Second optional capture group contains the extension (the last dot and everything after)
    // Example for "my.example.file.txt":
    // First group: "my.example.file"
    // Second group: ".txt"
    return fileName?.match(/^(.*?)(?:(\.[^.]+))?$/);
}
